import type { ApiHook, ApiHookReturn } from '@frontend/api-client';
import { hasPaymentMethod, hasSufficientCredits, isFrictionlessSignupUser } from '@frontend/api-client';
import type { Credit, TimeSlot } from '@frontend/api-types';
import type { getMutationHandler } from '@frontend/duck-tape';
import { isNil, pluralize } from '@frontend/duck-tape';
import { useFeatureValue, useMemo, useState } from '@frontend/react';
import { useTerminology } from '../../terminology';
import { useCreateDraftTask } from '../context/CreateDraftTaskContext';

type UseCheckoutStepProps = {
  createTaskFromDraftMutation: ApiHookReturn<'useCreateTaskFromDraftTaskMutation'>['0'];
  credit?: Credit;
  handleMutation: ReturnType<typeof getMutationHandler>;
  incrementParentStep: () => void;
  onTaskCreated: (taskId: string) => void;
  useRetrieveClientQuery: ApiHook<'useRetrieveClientQuery'>;
  useRetrieveClientStatusQuery: ApiHook<'useRetrieveClientStatusQuery'>;
};

export const useCheckoutStep = ({
  createTaskFromDraftMutation,
  credit,
  handleMutation,
  incrementParentStep,
  onTaskCreated,
  useRetrieveClientQuery,
  useRetrieveClientStatusQuery,
}: UseCheckoutStepProps) => {
  const { data: clientStatus } = useRetrieveClientStatusQuery();
  const { data: client } = useRetrieveClientQuery();
  const { pricingTerminology, showCredits } = useTerminology({ client, clientStatus });
  const { setState, state } = useCreateDraftTask();
  const [isCreateTaskLoading, setIsCreateTaskLoading] = useState(false);
  const { value: complexityBucketLabel = 'Task size' } = useFeatureValue('complexity_bucket_label');
  const taskCreditCost =
    state.draftTaskEntity && state.draftTaskEntity.creditCost !== null && state.draftTaskEntity.creditCost !== undefined
      ? state.draftTaskEntity.creditCost
      : 1;

  const hasAttachments = state.attachments.length > 0;

  const creditText = {
    cost: showCredits ? `${taskCreditCost} ${pluralize(pricingTerminology.unit_term, taskCreditCost)}` : undefined,
    remaining:
      showCredits && !isNil(credit)
        ? `${credit.availableCredits} ${pluralize(pricingTerminology.unit_term, credit.availableCredits)} remaining`
        : undefined,
  };

  const createTask = async () => {
    if (!state.draftTaskEntity?.id || !state.selectedTimeSlot) return;

    setIsCreateTaskLoading(true);

    const result = await handleMutation({
      mutation: async () => {
        return createTaskFromDraftMutation({
          draftTaskId: state.draftTaskEntity!.id,
          startSlot: state.selectedTimeSlot!,
        }).unwrap();
      },
      onSuccess: (data: { id: string } | undefined) => {
        if (data) {
          onTaskCreated(data.id);
          incrementParentStep();
        }
        return data;
      },
      showSuccessToast: false,
    });

    setIsCreateTaskLoading(false);
    return result;
  };

  const getTaskStartText = () => 'ASAP';

  const setSelectedTimeSlot = (timeSlot: TimeSlot) => {
    setState({ selectedTimeSlot: timeSlot });
  };

  const taskComplexityText = state.draftTaskEntity?.complexityBucket
    ? `${complexityBucketLabel}: ${state.draftTaskEntity.complexityBucket}`
    : null;

  const checkoutButtonType = useMemo(() => {
    if (hasSufficientCredits({ credits: credit, creditsCost: taskCreditCost })) {
      return 'createTask' as const;
    }

    if (isFrictionlessSignupUser(client)) {
      return 'addPaymentMethod' as const;
    }

    if (!hasPaymentMethod(clientStatus) && client?.isHouseholdOwner) {
      return 'addPaymentMethod' as const;
    }
    return 'buyCredits' as const;
  }, [client, credit, taskCreditCost, clientStatus]);

  return {
    checkoutButtonType,
    createTask,
    creditText,
    getTaskStartText,
    hasAttachments,
    isCreateTaskLoading,
    setSelectedTimeSlot,
    state,
    taskComplexityText,
    taskCreditCost,
  };
};
